import {
  MutableRefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from "react";
import { Control, FieldValues, useWatch } from "react-hook-form";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";

import { canFocusFormField } from "./focus-form-field.util";

export type Blocker = {
  /** Field name as the validator knows it — also the jump anchor. */
  name: string;
  label: string;
  message: string;
};

type FormBlockersProps = {
  blockers: Blocker[];
  onJump: (name: string) => void;
};

/**
 * Says what is stopping a save, beside the button it is stopping (DEV-2995).
 *
 * Deliberately not an Alert: this is a to-do list, not an error. Nothing has gone
 * wrong yet — the user has not even tried to save — so it reads as quiet guidance
 * and leaves red for what appears after Save is pressed.
 *
 * A blocker whose field isn't rendered is still listed but is NOT clickable — a
 * control that looks interactive and does nothing is the failure this exists to fix.
 */
export default function FormBlockers({ blockers, onJump }: FormBlockersProps) {
  const { palette } = useTheme();
  const [jumpable, setJumpable] = useState<string[]>([]);

  useLayoutEffect(() => {
    setJumpable(
      blockers.filter((b) => canFocusFormField(b.name)).map((b) => b.name)
    );
  }, [blockers]);

  if (blockers.length === 0) {
    return null;
  }

  const accent = palette.warning.main;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        bgcolor: alpha(accent, 0.07),
        border: 1,
        borderColor: alpha(accent, 0.22),
        "@media (prefers-reduced-motion: no-preference)": {
          animation: "blockersIn 220ms ease-out"
        },
        "@keyframes blockersIn": {
          from: { opacity: 0, transform: "translateY(6px)" },
          to: { opacity: 1, transform: "none" }
        }
      }}
    >
      <Box
        sx={{
          display: "grid",
          placeItems: "center",
          minWidth: 22,
          height: 22,
          px: 0.5,
          borderRadius: "11px",
          bgcolor: accent,
          color: palette.getContrastText(accent),
          fontSize: 12,
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums"
        }}
      >
        {blockers.length}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
        {blockers.length === 1 ? "thing needed" : "things needed"} before you
        can save
      </Typography>

      {blockers.map((blocker) => {
        const canJump = jumpable.includes(blocker.name);

        return (
          <Chip
            key={blocker.name}
            // Filled chips are flattened to one lilac by the app theme, so outlined
            // is the only variant whose colour survives.
            variant="outlined"
            size="small"
            label={blocker.label}
            {...(canJump
              ? { onClick: () => onJump(blocker.name) }
              : { disabled: true })}
            sx={{
              borderColor: alpha(accent, 0.5),
              bgcolor: palette.background.paper,
              fontWeight: 500,
              transition: "transform 150ms ease, border-color 150ms ease",
              "&:hover": canJump
                ? {
                    borderColor: accent,
                    bgcolor: alpha(accent, 0.1),
                    transform: "translateY(-1px)"
                  }
                : undefined,
              "@media (prefers-reduced-motion: reduce)": { transition: "none" }
            }}
          />
        );
      })}
    </Box>
  );
}

type LiveFormBlockersProps<T extends FieldValues> = {
  control?: Control<T>;
  getBlockers: (values: T) => Blocker[];
  /** Read by Save, which jumps to the first blocker without watching the form. */
  latestBlockers: MutableRefObject<Blocker[]>;
  onJump: (name: string) => void;
};

/**
 * The strip, watching the form itself. The watching has to live down here: a host
 * that watched every field to hand ModalForm a list re-rendered the whole form on
 * each keystroke — 1,577 component renders per character on the appointment form
 * (ENG-2045).
 */
export function LiveFormBlockers<T extends FieldValues>({
  control,
  getBlockers,
  latestBlockers,
  onJump
}: LiveFormBlockersProps<T>) {
  const values = useWatch({ control }) as T;
  const blockers = useMemo(() => getBlockers(values), [getBlockers, values]);

  useEffect(() => {
    latestBlockers.current = blockers;
  }, [blockers, latestBlockers]);

  return <FormBlockers blockers={blockers} onJump={onJump} />;
}
